// ============================================================
// STARTING COHESION (Squad Mode stage 6; ruled 2026-10-23: "formula + GM
// adjust, leaving space for Rank, Oath and the like"). Worked out LIVE from
// the kill-team on the sheets (plan modes-rework) with its breakdown, so the
// meter can say where every point came from.
//
//   the (acting) leader's Fellowship Bonus, its Unnatural multiplier read
//   + their Command advances (D2: trained 0, +10 one, +20 two - the app has
//     no +30 tier)
//   + the Oath's bonus
//   + the GM's adjustment
//
// never below zero. A new term is one more line in `TERMS`.
//
// Pure. Zero React dependency.
// ============================================================
import type { Character } from '../character/types';
import { characteristicBonus } from '../character/combatStatsResolver';
import type { Oath } from './tactics';

/** One line of the meter's breakdown. */
export interface CohesionLine {
  label: string;
  value: number;
}

type Leader = Pick<Character, 'name' | 'characteristics' | 'skills'>;
/** What the formula reads of an Oath: its name and bonus (a Tactics item's, or the copy a sheet carries). */
type OathTerm = Pick<Oath, 'name' | 'cohesionBonus'>;

function fellowship(leader: Leader): CohesionLine {
  const fel = leader.characteristics.fel;
  return {
    label: `${leader.name}'s Fellowship Bonus`,
    value: characteristicBonus(fel.value) * (fel.unnaturalMultiplier || 1),
  };
}

function command(leader: Leader): CohesionLine {
  const c = leader.skills.command;
  const value = c?.plus20 ? 2 : c?.plus10 ? 1 : 0;
  return { label: `Command +${value * 10}`, value };
}

/** What a kill-team with no leader shows where his terms would be. */
const NO_LEADER: CohesionLine = { label: 'No leader', value: 0 };

/**
 * The kill-team's starting Cohesion and where it came from; a line worth nothing is left out of the breakdown. Without
 * a leader his terms count nothing and the breakdown says "No leader" - the Oath's bonus and the adjustment still do.
 */
export function startingCohesion(
  leader: Leader | null,
  oath: OathTerm | null,
  adjustment: number,
): { start: number; breakdown: CohesionLine[] } {
  const counted = [
    ...(leader ? [fellowship(leader), command(leader)] : []),
    { label: oath?.name ?? 'Oath', value: oath?.cohesionBonus ?? 0 },
    { label: 'GM adjustment', value: adjustment },
  ].filter((line) => line.value !== 0);
  return {
    start: Math.max(
      0,
      counted.reduce((n, line) => n + line.value, 0),
    ),
    breakdown: leader ? counted : [NO_LEADER, ...counted],
  };
}
